import IconExpandMore from 'assets/icons/expand-more.svg';
import cn from 'classnames';
import Badge from 'components/badge';
import LazyMotionWrapper from 'components/lazy-motion-wrapper';
import Price from 'components/price';
import defaults from 'lodash/defaults';
import MotionDiv from 'modules/checkout/components/motion-div';
import { FC, ReactNode, useEffect, useState } from 'react';
import { RowProps } from '../../types';

const SubLine: FC<{ children: ReactNode; dataTest: string }> = ({ children, dataTest }) => (
  <p className="text-text-light-bg-secondary text-sm leading-tight" data-test={dataTest}>
    {children}
  </p>
);

export const Row: FC<RowProps> = ({
  expanded,
  dataTest,
  title,
  subLine,
  price,
  badge,
  breakdown = [],
}) => {
  const isExpandable = breakdown.length > 1;
  const shouldRenderToggle = isExpandable;
  const [isExpanded, setIsExpanded] = useState(
    isExpandable && typeof expanded === 'boolean' ? expanded : true
  );

  const test = defaults(dataTest, {
    breakdown: 'price-summary-row-breakdown',
    container: 'price-summary-row',
    price: 'price-summary-row-price',
    row: 'price-summary-row',
    subLine: 'price-summary-row-sub-line',
    toggle: 'price-summary-row-toggle',
    title: 'price-summary-row-title',
  });

  useEffect(() => {
    if (typeof expanded === 'boolean') {
      setIsExpanded(expanded);
    }
  }, [expanded]);

  const titleElement = (
    <div className="flex items-center gap-2">
      <span>{title}</span>

      {badge && (
        <Badge rounded={false} type={badge.type ?? 'Discount'} className="inline-block!">
          {badge.content}
        </Badge>
      )}

      {isExpandable && (
        <IconExpandMore
          className={cn(
            'ease h-5 w-5 shrink-0 transition-transform duration-200',
            isExpanded ? 'rotate-180' : 'rotate-0'
          )}
        />
      )}
    </div>
  );

  const mainRow = (
    <div className="flex items-start justify-between gap-4 text-sm" data-test={test.row}>
      <div data-test={`${test.title}-group`}>
        <div data-test={test.title}>{titleElement}</div>
        {subLine && <SubLine dataTest={test.subLine}>{subLine}</SubLine>}
      </div>
      <div className="shrink-0 text-right whitespace-nowrap" data-test={test.price}>
        {typeof price === 'number' ? <Price cents value={price} /> : price}
      </div>
    </div>
  );

  const breakdownContent = (
    <div className="flex flex-col gap-1 pl-3 text-sm" data-test={test.breakdown}>
      {breakdown.map((entry, index) => {
        const entryTest = defaults(entry.dataTest, {
          root: `price-summary-row-breakdown-entry-${index}`,
          title: `price-summary-row-breakdown-entry-${index}-title`,
          price: `price-summary-row-breakdown-entry-${index}-price`,
          subLine: `price-summary-row-breakdown-entry-${index}-sub-line`,
        });

        return (
          <div
            key={index}
            className="text-text-secondary flex items-start justify-between gap-4"
            data-test={entryTest.root}
          >
            <div data-test={`${entryTest.root}-text`}>
              <div className="flex items-center gap-2" data-test={entryTest.title}>
                <span>{entry.title}</span>
                {entry.badge && (
                  <Badge
                    rounded={false}
                    type={entry.badge.type ?? 'Discount'}
                    className="inline-block!"
                  >
                    {entry.badge.content}
                  </Badge>
                )}
              </div>
              {entry.subLine && <SubLine dataTest={entryTest.subLine}>{entry.subLine}</SubLine>}
            </div>
            <div className="shrink-0 text-right whitespace-nowrap" data-test={entryTest.price}>
              <Price cents value={entry.price} />
            </div>
          </div>
        );
      })}
    </div>
  );

  return (
    <div
      className="text-text-light-bg-primary flex flex-col gap-2 [&+&]:mt-2"
      data-test={test.container}
    >
      {!shouldRenderToggle && mainRow}
      {shouldRenderToggle && (
        <button
          type="button"
          className={cn('w-full text-left', !isExpandable && 'cursor-default')}
          onClick={() => isExpandable && setIsExpanded((previous) => !previous)}
          data-test={test.toggle}
        >
          {mainRow}
        </button>
      )}

      {breakdown.length > 0 && !isExpandable && breakdownContent}

      {isExpandable && (
        <LazyMotionWrapper>
          <MotionDiv display={isExpanded}>{breakdownContent}</MotionDiv>
        </LazyMotionWrapper>
      )}
    </div>
  );
};
